/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For mor information visit: https://egtuniverse.com/legal
 * 
 * Created for Universe App Tools.
 * 
 * Created by Justin K Kazmierczak.
 */
var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.select.color",
  title: "Color Picker",
  description: "A popup color picker control that saves HEX or RGBA.",
  control: true,
  prevalidate: true,
  supportServer: false,
  fields: {
    "name": {
      title: "Name",
      type: "string",
      description: "The name of the control.",
      required: true
    },
    "title": {
      title: "Title",
      type: "string",
      description: "The title of the control.",
      required: true
    },
    "allowOpacity": {
      title: "Allow Opacity",
      type: "boolean",
      description: "When true, the control returns RGBA. When false, it returns HEX.",
      default: false,
      addtouae: true
    },
    "required": {
      title: "Required",
      type: "boolean",
      description: "Whether the control requires a color value.",
      default: false,
      addtouae: true
    },
    "value": {
      title: "Value",
      type: "color",
      description: "Initial value. Accepts any valid CSS color.",
      required: false
    },
    "description": {
      title: "Description",
      type: "inner",
      description: "Helpful text shown below the control."
    }
  },
  errors: {
    "required": {
      title: "Required",
      description: "Please select a color.",
      isValidation: true
    },
    "invalidColor": {
      title: "Invalid Color",
      description: "The value must be a valid CSS color.",
      isValidation: true
    }
  },
  passthrough: {
    except: ["inner", "type", "id", "allowopacity"]
  }
});

var jsonRender = require("../interface/jsonRender.js");
var uai = require("../interface/interface.js");

function parseBoolean(value, fallback) {
  if (value === undefined || value === null) {
    return fallback;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    var normalized = value.toLowerCase().trim();
    if (normalized === "true") { return true; }
    if (normalized === "false") { return false; }
  }

  return Boolean(value);
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function toTwoHexChars(value) {
  var hex = value.toString(16).toUpperCase();
  return hex.length === 1 ? "0" + hex : hex;
}

function rgbToHex(red, green, blue) {
  return "#" + toTwoHexChars(red) + toTwoHexChars(green) + toTwoHexChars(blue);
}

function parseRgbString(value) {
  if (typeof value !== "string") {
    return null;
  }

  var match = value
    .trim()
    .match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*([+-]?\d*\.?\d+))?\s*\)$/i);

  if (!match) {
    return null;
  }

  var red = Number(match[1]);
  var green = Number(match[2]);
  var blue = Number(match[3]);
  var alpha = match[4] === undefined ? 1 : Number(match[4]);

  if (
    Number.isNaN(red) || Number.isNaN(green) || Number.isNaN(blue) || Number.isNaN(alpha) ||
    red < 0 || red > 255 ||
    green < 0 || green > 255 ||
    blue < 0 || blue > 255 ||
    alpha < 0 || alpha > 1
  ) {
    return null;
  }

  return { red, green, blue, alpha };
}

function parseCssColor(value) {
  if (typeof value !== "string") {
    return null;
  }

  var trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }

  var legacyTuple = parseLegacyColorTuple(trimmed);
  if (legacyTuple) {
    return legacyTuple;
  }

  return parseStandardCssColor(trimmed);
}

function parseStandardCssColor(trimmed) {
  if (typeof trimmed !== "string" || trimmed === "") {
    return null;
  }

  var probe = document.createElement("span");
  probe.style.color = "";
  probe.style.color = trimmed;

  if (!probe.style.color) {
    return null;
  }

  probe.style.display = "none";
  var parent = document.body || document.documentElement;
  if (!parent) {
    return parseRgbString(probe.style.color);
  }

  parent.appendChild(probe);
  var computedColor = window.getComputedStyle(probe).color;
  probe.remove();

  return parseRgbString(computedColor);
}

function parseLegacyColorTuple(value) {
  if (typeof value !== "string") {
    return null;
  }

  var trimmed = value.trim();
  if (trimmed === "" || trimmed.indexOf(",") === -1) {
    return null;
  }

  if (/^rgba?\(/i.test(trimmed) || /^hsla?\(/i.test(trimmed)) {
    return null;
  }

  var tokens = trimmed.split(",").map(function (entry) {
    return entry.trim();
  }).filter(function (entry) {
    return entry !== "";
  });

  if (tokens.length < 2) {
    return null;
  }

  var alpha = null;
  var firstToken = tokens[0];
  var firstTokenAsNumber = Number(firstToken);

  if (!Number.isNaN(firstTokenAsNumber)) {
    alpha = firstTokenAsNumber > 1 ? firstTokenAsNumber / 100 : firstTokenAsNumber;
    alpha = clamp(alpha, 0, 1);
  }

  for (var i = 0; i < tokens.length; i++) {
    var parsed = parseStandardCssColor(tokens[i]);
    if (parsed) {
      if (alpha !== null) {
        parsed.alpha = alpha;
      }
      return parsed;
    }
  }

  return null;
}

function alphaToLabel(alpha) {
  var rounded = Math.round(alpha * 1000) / 1000;
  return String(rounded).replace(/\.?0+$/, "");
}

function isHexOutput(value) {
  return typeof value === "string" && /^#[0-9A-F]{6}$/.test(value);
}

function isRgbaOutput(value) {
  if (typeof value !== "string") {
    return false;
  }

  var match = value.match(/^rgba\((\d{1,3}),(\d{1,3}),(\d{1,3}),(0|1|0?\.\d+)\)$/);
  if (!match) {
    return false;
  }

  var red = Number(match[1]);
  var green = Number(match[2]);
  var blue = Number(match[3]);
  var alpha = Number(match[4]);

  return (
    !Number.isNaN(red) &&
    !Number.isNaN(green) &&
    !Number.isNaN(blue) &&
    !Number.isNaN(alpha) &&
    red >= 0 && red <= 255 &&
    green >= 0 && green <= 255 &&
    blue >= 0 && blue <= 255 &&
    alpha >= 0 && alpha <= 1
  );
}

function isSerializedColorOutput(value) {
  return isHexOutput(value) || isRgbaOutput(value);
}

function toRgbaString(parsed) {
  return "rgba(" +
    parsed.red + "," +
    parsed.green + "," +
    parsed.blue + "," +
    alphaToLabel(parsed.alpha) +
    ")";
}

function normalizeHex(value) {
  if (typeof value !== "string") {
    return null;
  }

  var trimmed = value.trim();
  var match = trimmed.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) {
    return null;
  }

  var hex = match[1].toUpperCase();
  if (hex.length === 3) {
    return "#" + hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  return "#" + hex;
}

function normalizeColor(parsed, allowOpacity, rawValue) {
  if (!parsed) {
    return "";
  }

  if (allowOpacity) {
    return toRgbaString(parsed);
  }

  var hexValue = rgbToHex(parsed.red, parsed.green, parsed.blue);
  var normalizedRawHex = normalizeHex(rawValue);

  if (normalizedRawHex && normalizedRawHex !== hexValue) {
    return toRgbaString({
      red: parsed.red,
      green: parsed.green,
      blue: parsed.blue,
      alpha: 1
    });
  }

  return hexValue;
}

function rgbToHsv(red, green, blue) {
  var r = red / 255;
  var g = green / 255;
  var b = blue / 255;

  var max = Math.max(r, g, b);
  var min = Math.min(r, g, b);
  var delta = max - min;

  var hue = 0;
  if (delta !== 0) {
    if (max === r) {
      hue = ((g - b) / delta) % 6;
    } else if (max === g) {
      hue = ((b - r) / delta) + 2;
    } else {
      hue = ((r - g) / delta) + 4;
    }
  }

  hue = Math.round(hue * 60);
  if (hue < 0) {
    hue += 360;
  }

  var saturation = max === 0 ? 0 : delta / max;
  var value = max;

  return {
    hue: clamp(hue, 0, 360),
    saturation: clamp(saturation, 0, 1),
    value: clamp(value, 0, 1)
  };
}

function hsvToRgb(hue, saturation, value) {
  var h = (hue % 360 + 360) % 360;
  var c = value * saturation;
  var x = c * (1 - Math.abs((h / 60) % 2 - 1));
  var m = value - c;

  var rPrime = 0;
  var gPrime = 0;
  var bPrime = 0;

  if (h < 60) {
    rPrime = c; gPrime = x; bPrime = 0;
  } else if (h < 120) {
    rPrime = x; gPrime = c; bPrime = 0;
  } else if (h < 180) {
    rPrime = 0; gPrime = c; bPrime = x;
  } else if (h < 240) {
    rPrime = 0; gPrime = x; bPrime = c;
  } else if (h < 300) {
    rPrime = x; gPrime = 0; bPrime = c;
  } else {
    rPrime = c; gPrime = 0; bPrime = x;
  }

  return {
    red: Math.round((rPrime + m) * 255),
    green: Math.round((gPrime + m) * 255),
    blue: Math.round((bPrime + m) * 255)
  };
}

function readAllowOpacity(control) {
  return parseBoolean(control.getAttribute("allowopacity"), false);
}

function getParts(control) {
  return {
    wrapper: control.querySelector(".ua-color-picker-wrap"),
    panel: control.querySelector(".ua-color-picker-panel"),
    trigger: control.querySelector(".ua-color-picker-trigger"),
    swatch: control.querySelector(".ua-color-picker-swatch"),
    triggerText: control.querySelector(".ua-color-picker-trigger-text"),
    map: control.querySelector(".ua-color-picker-map"),
    mapCursor: control.querySelector(".ua-color-picker-map-cursor"),
    hue: control.querySelector(".ua-color-picker-hue"),
    hueValue: control.querySelector(".ua-color-picker-hue-value"),
    alpha: control.querySelector(".ua-color-picker-alpha"),
    alphaValue: control.querySelector(".ua-color-picker-alpha-value"),
    alphaGradient: control.querySelector(".ua-color-picker-alpha-gradient"),
    native: control.querySelector(".ua-color-picker-native"),
    input: control.querySelector(".ua-color-picker-value"),
    swatches: control.querySelectorAll(".ua-color-picker-preset"),
    closeBtn: control.querySelector(".ua-color-picker-close")
  };
}

function getState(control) {
  if (!control.__uaColorState) {
    control.__uaColorState = {
      hue: 0,
      saturation: 1,
      value: 1,
      alpha: 1
    };
  }
  return control.__uaColorState;
}

function paintMap(control) {
  var parts = getParts(control);
  var state = getState(control);

  if (!parts.map) {
    return;
  }

  var hueRgb = hsvToRgb(state.hue, 1, 1);
  parts.map.style.backgroundColor = rgbToHex(hueRgb.red, hueRgb.green, hueRgb.blue);
}

function paintMapCursor(control) {
  var parts = getParts(control);
  var state = getState(control);

  if (!parts.mapCursor) {
    return;
  }

  parts.mapCursor.style.left = (state.saturation * 100) + "%";
  parts.mapCursor.style.top = ((1 - state.value) * 100) + "%";
}

function applyStateToControls(control) {
  var parts = getParts(control);
  var state = getState(control);

  if (parts.hue) {
    parts.hue.value = String(Math.round(state.hue));
  }

  if (parts.hueValue) {
    parts.hueValue.innerText = String(Math.round(state.hue)) + "deg";
  }

  if (parts.alpha) {
    parts.alpha.value = String(Math.round(state.alpha * 100));
  }

  if (parts.alphaValue) {
    parts.alphaValue.innerText = alphaToLabel(state.alpha);
  }
}

function syncColorOutputs(control) {
  var parts = getParts(control);
  var state = getState(control);

  if (!parts.input || !parts.swatch || !parts.triggerText) {
    return;
  }

  var rgb = hsvToRgb(state.hue, state.saturation, state.value);
  var parsed = {
    red: rgb.red,
    green: rgb.green,
    blue: rgb.blue,
    alpha: state.alpha
  };

  var allowOpacity = readAllowOpacity(control);
  var value = normalizeColor(parsed, allowOpacity);

  parts.input.value = value;
  parts.swatch.style.backgroundColor = toRgbaString(parsed);
  parts.triggerText.innerText = value;

  if (parts.native) {
    parts.native.value = rgbToHex(parsed.red, parsed.green, parsed.blue);
  }

  if (parts.alphaGradient) {
    var solidColor = "rgb(" + parsed.red + ", " + parsed.green + ", " + parsed.blue + ")";
    parts.alphaGradient.style.background = "linear-gradient(to right, transparent, " + solidColor + ")";
  }
}

function setStateFromParsedColor(control, parsed, options) {
  var state = getState(control);
  var hsv = rgbToHsv(parsed.red, parsed.green, parsed.blue);

  state.hue = hsv.hue;
  state.saturation = hsv.saturation;
  state.value = hsv.value;

  if (!(options && options.keepAlpha)) {
    state.alpha = clamp(parsed.alpha, 0, 1);
  }

  paintMap(control);
  paintMapCursor(control);
  applyStateToControls(control);
  syncColorOutputs(control);
}

function setDefaultState(control) {
  var state = getState(control);
  state.hue = 0;
  state.saturation = 1;
  state.value = 1;
  state.alpha = 1;

  paintMap(control);
  paintMapCursor(control);
  applyStateToControls(control);
  syncColorOutputs(control);
}

function parseControlColor(control) {
  var parts = getParts(control);
  if (!parts.input) {
    return null;
  }

  return parseCssColor(parts.input.value || "");
}

function validate(control) {
  var parts = getParts(control);
  var required = parseBoolean(control.getAttribute("required"), false);
  var allowOpacity = readAllowOpacity(control);
  var raw = parts.input ? (parts.input.value || "").trim() : "";
  var errors = [];

  if (required && raw === "") {
    errors.push({
      type: "validation",
      message: _.errors.required.description
    });
    return errors;
  }

  if (raw !== "" && !(parseCssColor(raw))) {
    errors.push({
      type: "validation",
      message: _.errors.invalidColor.description
    });
    return errors;
  }

  if (raw !== "") {
    var parsed = parseCssColor(raw);
    var normalized = normalizeColor(parsed, allowOpacity, raw);
    if (!isSerializedColorOutput(normalized)) {
      errors.push({
        type: "validation",
        message: _.errors.invalidColor.description
      });
    }
  }

  return errors;
}

function clampAndSetFromMap(control, clientX, clientY) {
  var parts = getParts(control);
  var state = getState(control);

  if (!parts.map) {
    return;
  }

  var rect = parts.map.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return;
  }

  var x = clamp((clientX - rect.left) / rect.width, 0, 1);
  var y = clamp((clientY - rect.top) / rect.height, 0, 1);

  state.saturation = x;
  state.value = 1 - y;

  paintMapCursor(control);
  syncColorOutputs(control);
}

function registerPointerMap(control) {
  var parts = getParts(control);
  if (!parts.map) {
    return;
  }

  var isDragging = false;

  parts.map.addEventListener("pointerdown", function (event) {
    isDragging = true;
    if (parts.map.setPointerCapture) {
      try {
        parts.map.setPointerCapture(event.pointerId);
      } catch (error) {
        console.warn("ua.select.color pointer capture failed", error);
      }
    }

    clampAndSetFromMap(control, event.clientX, event.clientY);
  });

  parts.map.addEventListener("pointermove", function (event) {
    if (!isDragging) {
      return;
    }

    clampAndSetFromMap(control, event.clientX, event.clientY);
  });

  var stopDrag = function () {
    isDragging = false;
  };

  parts.map.addEventListener("pointerup", stopDrag);
  parts.map.addEventListener("pointercancel", stopDrag);
  parts.map.addEventListener("lostpointercapture", stopDrag);
}

function registerPresetEvents(control) {
  var parts = getParts(control);

  if (!parts.swatches || parts.swatches.length === 0) {
    return;
  }

  for (var i = 0; i < parts.swatches.length; i++) {
    parts.swatches[i].addEventListener("click", function () {
      var value = this.getAttribute("data-color") || "";
      var parsed = parseCssColor(value);
      if (parsed) {
        setStateFromParsedColor(control, parsed, { keepAlpha: true });
      }
    });
  }
}

function setupEvents(control) {
  var parts = getParts(control);
  if (!parts.wrapper || !parts.panel || !parts.trigger || !parts.input) {
    return;
  }

  var isMobilePanel = false;

  function openPanel() {
    parts.panel.classList.remove("d-none");
    parts.trigger.setAttribute("aria-expanded", "true");

    if (window.innerWidth < 576) {
      isMobilePanel = true;
      parts.panel.style.position = "fixed";
      parts.panel.style.inset = "0";
      parts.panel.style.width = "100%";
      parts.panel.style.maxWidth = "100%";
      parts.panel.style.height = "100%";
      parts.panel.style.zIndex = "9999";
      parts.panel.style.borderRadius = "0";
      parts.panel.style.overflowY = "auto";
      parts.panel.style.top = "";
      parts.panel.style.bottom = "";
    } else {
      isMobilePanel = false;
      parts.panel.style.position = "";
      parts.panel.style.inset = "";
      parts.panel.style.width = "";
      parts.panel.style.maxWidth = "";
      parts.panel.style.height = "";
      parts.panel.style.zIndex = "";
      parts.panel.style.borderRadius = "";
      parts.panel.style.overflowY = "";

      var rect = parts.trigger.getBoundingClientRect();
      var spaceBelow = window.innerHeight - rect.bottom;
      var spaceAbove = rect.top;
      if (spaceBelow < 300 && spaceAbove > spaceBelow) {
        parts.panel.style.top = "auto";
        parts.panel.style.bottom = "calc(100% + 0.5rem)";
      } else {
        parts.panel.style.top = "calc(100% + 0.5rem)";
        parts.panel.style.bottom = "auto";
      }
    }
  }

  function closePanel() {
    parts.panel.classList.add("d-none");
    parts.trigger.setAttribute("aria-expanded", "false");
    isMobilePanel = false;
    parts.panel.style.position = "";
    parts.panel.style.inset = "";
    parts.panel.style.width = "";
    parts.panel.style.maxWidth = "";
    parts.panel.style.height = "";
    parts.panel.style.zIndex = "";
    parts.panel.style.borderRadius = "";
    parts.panel.style.overflowY = "";
    parts.panel.style.top = "";
    parts.panel.style.bottom = "";
  }

  parts.trigger.addEventListener("click", function () {
    if (parts.panel.classList.contains("d-none")) {
      openPanel();
      return;
    }
    closePanel();
  });

  parts.wrapper.addEventListener("focusout", function () {
    if (isMobilePanel) {
      return;
    }
    setTimeout(function () {
      if (!parts.wrapper.contains(document.activeElement)) {
        closePanel();
      }
    }, 0);
  });

  parts.wrapper.addEventListener("keydown", function (event) {
    if (event.key === "Escape" && !parts.panel.classList.contains("d-none")) {
      closePanel();
      parts.trigger.focus();
    }
  });

  if (parts.closeBtn) {
    parts.closeBtn.addEventListener("click", function () {
      closePanel();
      parts.trigger.focus();
    });
  }

  if (parts.hue) {
    parts.hue.addEventListener("input", function () {
      var state = getState(control);
      state.hue = clamp(Number(parts.hue.value || 0), 0, 360);
      paintMap(control);
      applyStateToControls(control);
      syncColorOutputs(control);
    });
  }

  if (parts.alpha) {
    parts.alpha.addEventListener("input", function () {
      var state = getState(control);
      state.alpha = clamp(Number(parts.alpha.value || 100) / 100, 0, 1);
      applyStateToControls(control);
      syncColorOutputs(control);
    });
  }

  if (parts.native) {
    parts.native.addEventListener("input", function () {
      var parsed = parseCssColor(parts.native.value);
      if (parsed) {
        setStateFromParsedColor(control, {
          red: parsed.red,
          green: parsed.green,
          blue: parsed.blue,
          alpha: getState(control).alpha
        }, { keepAlpha: true });
      }
    });
  }

  parts.input.addEventListener("change", function () {
    var parsed = parseCssColor(parts.input.value);
    if (parsed) {
      setStateFromParsedColor(control, parsed);
    }
  });

  registerPointerMap(control);
  registerPresetEvents(control);
}

function makePreset(color) {
  return {
    "n": "button",
    "type": "button",
    "class": "ua-color-picker-preset border rounded",
    "data-color": color,
    "title": color,
    "style": "width: 1.5rem; height: 1.5rem; background-color: " + color + ";"
  };
}

async function render(options) {
  var randomName = uai.generateRandomID(12);
  var allowOpacity = parseBoolean(options.allowOpacity, false);

  var panelChildren = [
    {
      "n": "div",
      "class": "d-flex justify-content-between align-items-center mb-3 d-sm-none",
      "i": [
        {
          "n": "span",
          "class": "fw-semibold",
          "i": "Choose a color"
        },
        {
          "n": "button",
          "type": "button",
          "class": "btn btn-sm btn-primary ua-color-picker-close",
          "i": "Done"
        }
      ]
    },
    {
      "n": "div",
      "class": "ua-color-picker-map mb-3 position-relative rounded",
      "style": "height: 11rem; cursor: crosshair; touch-action: none; overflow: hidden; background-image: linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent);",
      "i": {
        "n": "span",
        "class": "ua-color-picker-map-cursor position-absolute border border-2 border-white rounded-circle shadow",
        "style": "width: .95rem; height: .95rem; transform: translate(-50%, -50%); left: 100%; top: 0%;"
      }
    },
    {
      "n": "div",
      "c": "mb-3",
      "i": [
        {
          "n": "div",
          "c": "d-flex justify-content-between",
          "i": [
            {
              "n": "span",
              "class": "small",
              "i": "Hue"
            },
            {
              "n": "span",
              "class": "small text-muted ua-color-picker-hue-value",
              "i": "0deg"
            }
          ]
        },
        {
          "n": "div",
          "style": "height: 0.375rem; border-radius: 0.25rem; margin-bottom: 0.375rem; background: linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%);"
        },
        {
          "n": "input",
          "type": "range",
          "min": "0",
          "max": "360",
          "value": "0",
          "class": "form-range ua-color-picker-hue"
        }
      ]
    }
  ];

  if (allowOpacity) {
    panelChildren.push({
      "n": "div",
      "c": "mb-3",
      "i": [
        {
          "n": "div",
          "c": "d-flex justify-content-between",
          "i": [
            {
              "n": "span",
              "class": "small",
              "i": "Opacity"
            },
            {
              "n": "span",
              "class": "small text-muted ua-color-picker-alpha-value",
              "i": "1"
            }
          ]
        },
        {
          "n": "div",
          "class": "position-relative",
          "style": "height: 0.375rem; border-radius: 0.25rem; margin-bottom: 0.375rem; background-image: linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%); background-size: 0.5rem 0.5rem; background-color: #fff;",
          "i": {
            "n": "div",
            "class": "ua-color-picker-alpha-gradient",
            "style": "position: absolute; inset: 0; border-radius: 0.25rem; background: linear-gradient(to right, transparent, #FF0000);"
          }
        },
        {
          "n": "input",
          "type": "range",
          "min": "0",
          "max": "100",
          "value": "100",
          "class": "form-range ua-color-picker-alpha"
        }
      ]
    });
  }

  panelChildren.push({
    "n": "div",
    "c": "d-flex align-items-center gap-2 mb-3",
    "i": [
      {
        "n": "input",
        "type": "color",
        "value": "#FF0000",
        "class": "form-control form-control-color ua-color-picker-native",
        "title": "Color"
      },
      {
        "n": "div",
        "class": "d-flex flex-wrap gap-2",
        "i": [
          makePreset("#FF0000"),
          makePreset("#FF7A00"),
          makePreset("#FFD400"),
          makePreset("#2ECC71"),
          makePreset("#00AEEF"),
          makePreset("#4B61FF"),
          makePreset("#8E44AD"),
          makePreset("#FFFFFF"),
          makePreset("#111111")
        ]
      }
    ]
  });

  panelChildren.push({
    "n": "div",
    "c": "mb-1",
    "i": [
      {
        "n": "label",
        "for": randomName,
        "class": "form-label small mb-1",
        "i": allowOpacity ? "Hex / RGBA" : "Hex"
      },
      {
        "n": "input",
        "id": randomName,
        "type": "text",
        "class": "form-control ua-color-picker-value",
        "placeholder": allowOpacity ? "rgba(255, 255, 255, .5)" : "#FFFFFF"
      }
    ]
  });

  var root = {
    "n": "div",
    "name": options.name,
    "c": "form-group pb-3",
    "i": [
      {
        "n": "label",
        "class": "pb-2 fs-4",
        "i": options.title
      },
      {
        "n": "div",
        "class": "ua-color-picker-wrap position-relative",
        "i": [
          {
            "n": "button",
            "type": "button",
            "class": "btn btn-outline-secondary w-100 text-start d-flex align-items-center gap-2 ua-color-picker-trigger",
            "aria-expanded": "false",
            "i": [
              {
                "n": "span",
                "class": "ua-color-picker-swatch rounded border",
                "style": "width: 2rem; height: 2rem; display: inline-block; background-color: transparent;"
              },
              {
                "n": "span",
                "class": "ua-color-picker-trigger-text text-truncate",
                "i": "Choose a color"
              }
            ]
          },
          {
            "n": "div",
            "class": "ua-color-picker-panel d-none border rounded shadow-sm p-3 bg-body",
            "style": "position: absolute; z-index: 20; width: min(22rem, calc(100vw - 2rem)); max-width: 100%;",
            "i": panelChildren
          }
        ]
      }
    ]
  };

  if ("description" in options) {
    root.i.push({
      "n": "div",
      "class": "form-text mt-2",
      "i": options.description
    });
  }

  var domElement = await jsonRender.render(root);

  var wrapper = domElement.querySelector(".ua-color-picker-wrap");
  if (wrapper) {
    wrapper.setAttribute("tabindex", "0");
  }

  setupEvents(domElement);

  setDefaultState(domElement);

  var hasInitialValue = (
    "value" in options &&
    options.value !== undefined &&
    options.value !== null &&
    String(options.value).trim() !== ""
  );

  if (hasInitialValue) {
    var parsed = parseCssColor(String(options.value));
    if (parsed) {
      setStateFromParsedColor(domElement, parsed);
    }
  } else {
    var emptyParts = getParts(domElement);
    if (emptyParts.input) { emptyParts.input.value = ""; }
    if (emptyParts.triggerText) { emptyParts.triggerText.innerText = "Choose a color"; }
    if (emptyParts.swatch) { emptyParts.swatch.style.backgroundColor = "transparent"; }
  }

  return domElement;
}
module.exports.render = render;

async function save(name, control, repo) {
  control.classList.remove("is-invalid");
  control.classList.remove("is-valid");

  repo.errors = validate(control);
  repo.success = repo.errors.length === 0;

  if (!repo.success) {
    repo.data = null;
    control.classList.add("is-invalid");
    return repo;
  }

  var allowOpacity = readAllowOpacity(control);
  var parsed = parseControlColor(control);
  var parts = getParts(control);
  var rawValue = parts.input ? parts.input.value : "";
  repo.data = normalizeColor(parsed, allowOpacity, rawValue);
  if (!isSerializedColorOutput(repo.data)) {
    repo.success = false;
    repo.data = null;
    repo.errors.push({
      type: "validation",
      message: _.errors.invalidColor.description
    });
    control.classList.add("is-invalid");
    return repo;
  }

  control.classList.add("is-valid");
  return repo;
}
module.exports.save = save;

async function load(name, control, data) {
  var raw = "";

  if (typeof data === "string") {
    raw = data;
  } else if (data !== undefined && data !== null) {
    raw = String(data);
  }

  if (raw.trim() === "") {
    setDefaultState(control);
    return;
  }

  var parsed = parseCssColor(raw);
  if (!parsed) {
    var parts = getParts(control);
    if (parts.input) {
      parts.input.value = raw;
    }
    return;
  }

  setStateFromParsedColor(control, parsed);
}
module.exports.load = load;
